import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useGlobalization } from '@Translations';
import { StylovyzeDialog, Text, success, error, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { Grid, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CurveMethod, WeibullFormulation } from '@Types/deteriorationModels.types';
import { selectDeteriorationModel } from '@Selectors';
import { ChartSection } from '../ChartSection';
import { CurveFitNotes } from '../CurveFitNotes';
import { ConfigurationSection } from '../ConfigurationSection';
import { generateAvailableDataSeries } from '../utils';
import { useBulkCurveConfig } from './hooks';
import { ALL_CURVE_CONFIGS_SLICE, FAILURE_COUNT_THRESHOLD } from '../constants';
import { updateModelCurveConfigs } from '@Services/deteriorationModels.services';
import {
	listDeteriorationModelCurveConfigsAction,
	getGlobalUndefinedCountAction,
	getDeteriorationModelStatus,
} from '@Actions';
import { baseChartOptions } from '@Components/deteriorationModel/utils/chartDefaults';

interface BulkChangeDeteriorationMethodDialogProps {
	open: boolean;
	onCancel: () => void;
	cancelText: string;
	confirmText: string;
	selectedCurveConfigs: string[];
	selectedCurveConfigsData: Array<{
		id: string;
		assetGroupId: string;
		failureCount: number;
		selectedCurveMethod: CurveMethod;
		[key: string]: unknown;
	}>;
	modelId: string;
	industryStandardCurves: {
		curves: { key: string; displayName: string }[];
		isLoading: boolean;
	};
}

export const BulkChangeDeteriorationMethodDialog = ({
	open,
	onCancel,
	cancelText,
	confirmText,
	selectedCurveConfigs,
	selectedCurveConfigsData,
	modelId,
	industryStandardCurves,
}: BulkChangeDeteriorationMethodDialogProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const [selectedLines, setSelectedLines] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const deteriorationModel = selectDeteriorationModel();
	const weibullFormulation =
		deteriorationModel.deteriorationModel?.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER;
	const isWeibull3ParamEnabled = useIsFeatureEnabled('info360-weibull-3-param');

	const bulkCurveState = useBulkCurveConfig(modelId, industryStandardCurves);

	// Check if all selected groups have sufficient failures for "From Failure Definition"
	const allGroupsHaveSufficientFailures = selectedCurveConfigsData.every(
		config => config.failureCount >= FAILURE_COUNT_THRESHOLD,
	);

	// Generate chart data - exclude failure definition
	const availableDataSeries = useMemo(() => {
		const series = generateAvailableDataSeries(
			t,
			bulkCurveState.customCurvePoints,
			bulkCurveState.industryStandardCurvePoints,
			[], // Empty failure definition points as they won't be plotted
			bulkCurveState.copiedCurvePoints,
		);
		// Filter out failure definition from chart
		return series.filter(s => s.id !== CurveMethod.FAILURE_DEFINITION);
	}, [bulkCurveState, t]);

	// Auto-select chart lines when curve method changes
	useEffect(() => {
		const currentMethodSeries = availableDataSeries.find(series => series.id === bulkCurveState.selectedCurveMethod);

		if (currentMethodSeries && currentMethodSeries.data.length > 0) {
			// Add the selected method to chart if it has data and isn't already selected
			setSelectedLines(prev => {
				if (!prev.includes(bulkCurveState.selectedCurveMethod)) {
					return [...prev, bulkCurveState.selectedCurveMethod];
				}
				return prev;
			});
		} else if (bulkCurveState.selectedCurveMethod === CurveMethod.UNDEFINED) {
			// Clear selection when method is undefined
			setSelectedLines([]);
		}
	}, [
		bulkCurveState.selectedCurveMethod,
		bulkCurveState.customCurvePoints.length,
		bulkCurveState.industryStandardCurvePoints.length,
		bulkCurveState.copiedCurvePoints.length,
		bulkCurveState.industryStandardCurve,
		bulkCurveState.copyCurve,
	]);

	const chartOptions = useMemo(() => {
		const selectedSeries = availableDataSeries
			.filter(series => selectedLines.includes(series.id))
			.map(series => {
				const isSelectedMethod = series.id === bulkCurveState.selectedCurveMethod;
				return {
					type: 'spline' as const,
					name: series.name,
					data: [...series.data],
					color: series.color,
					// Highlight selected method
					opacity: isSelectedMethod ? 1 : 0.6,
					lineWidth: isSelectedMethod ? 3 : 2,
					marker: {
						enabled: true,
						radius: isSelectedMethod ? 5 : 3,
						symbol: isSelectedMethod ? 'circle' : 'circle',
					},
					zIndex: isSelectedMethod ? 10 : 1, // Bring selected method to front
				};
			});

		return {
			...baseChartOptions,
			chart: {
				type: 'spline',
			},
			title: {
				text: '',
			},
			xAxis: {
				title: {
					text: 'Time (Years)',
				},
			},
			yAxis: {
				title: {
					text: 'Survival Probability (%)',
				},
			},
			legend: {
				enabled: selectedSeries.length > 1,
			},
			series: selectedSeries,
			tooltip: {
				shared: true,
				crosshairs: true,
			},
		};
	}, [selectedLines, availableDataSeries, bulkCurveState.selectedCurveMethod]);

	const handleConfirm = useCallback(async () => {
		if (bulkCurveState.selectedCurveMethod === CurveMethod.UNDEFINED) {
			return;
		}

		setIsSaving(true);
		try {
			const methodData = bulkCurveState.setCurveMethodData();

			// Build curve configs for all selected items, excluding source curve if copying
			const curveConfigs = selectedCurveConfigs
				.filter(curveConfigId => {
					// When copying from another curve, exclude the source curve to prevent copying from itself
					const isSourceCurve =
						bulkCurveState.selectedCurveMethod === CurveMethod.COPIED_CURVE &&
						bulkCurveState.copyCurve === curveConfigId;
					return !isSourceCurve;
				})
				.map(curveConfigId => ({
					curveConfigId,
					selectedCurveMethod: methodData.selectedCurveMethod,
					copiedCurve: methodData.copiedCurve ?? undefined,
					industryCurve: methodData.industryCurve ?? undefined,
					oneExpectedLife: methodData.oneExpectedLife ?? undefined,
					customCurve: methodData.customCurve
						.filter((point): point is { age: number; survivalProbability: number } => point.age !== null)
						.map(point => ({ age: point.age, survivalProbability: point.survivalProbability })),
					// Only send weibullFormulation when the 3P flag is on, so a bulk save with the flag off
					// doesn't overwrite groups previously saved as 3-parameter with the 2P default.
					...(isWeibull3ParamEnabled ? { weibullFormulation: methodData.weibullFormulation } : {}),
				}));

			await updateModelCurveConfigs({
				modelId,
				weibullFormulation,
				curveConfigs,
			});

			dispatch(
				listDeteriorationModelCurveConfigsAction({
					modelId,
					page: 1,
					slice: ALL_CURVE_CONFIGS_SLICE,
					sortKey: 'assetGroupName',
					sortDescending: true,
					searchValue: null,
					filter: null,
					filterModel: { items: [] },
				}),
			);
			dispatch(getGlobalUndefinedCountAction({ modelId }));
			dispatch(getDeteriorationModelStatus({ id: modelId }));
			dispatch(success(t('Curve methods saved successfully')));
			onCancel(); // Close dialog
		} catch (e) {
			console.error('Error saving curve methods:', e);
			dispatch(error(t('Failed to save curve methods')));
		} finally {
			setIsSaving(false);
		}
	}, [
		bulkCurveState,
		selectedCurveConfigs,
		modelId,
		weibullFormulation,
		isWeibull3ParamEnabled,
		dispatch,
		t,
		onCancel,
	]);

	return (
		<StylovyzeDialog
			open={open}
			title={t('Change Survival Curve Method')}
			onCancel={() => {
				if (!isSaving) onCancel();
			}}
			cancelText={cancelText}
			confirmText={isSaving ? t('Saving...') : confirmText}
			onConfirm={() => {
				if (!isSaving) handleConfirm();
			}}
			dialogProps={{
				fullWidth: true,
				maxWidth: 'lg',
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Stack direction="row" spacing={1}>
						<Text>{t('Apply a new method to the selected groups')}</Text>
						<Text>{selectedCurveConfigs.length.toString()}</Text>
					</Stack>
				</Grid>
				<Grid item xs={6}>
					<ChartSection
						availableDataSeries={availableDataSeries}
						selectedLines={selectedLines}
						setSelectedLines={setSelectedLines}
						chartOptions={chartOptions}
						footer={<CurveFitNotes curveState={bulkCurveState} />}
					/>
				</Grid>
				<Grid item xs={6}>
					<ConfigurationSection
						curveState={bulkCurveState}
						industryStandardCurves={industryStandardCurves}
						currentCurveConfigId="" // Not applicable for bulk changes
						modelId={modelId}
						failureCount={allGroupsHaveSufficientFailures ? 5 : 0}
						isBulkMode={true}
						hideButtons={true}
					/>
				</Grid>
			</Grid>
		</StylovyzeDialog>
	);
};
